/**
 * SSE hook for XAI chat progress updates.
 * 
 * Connects to /api/xai-chat/progress/{requestId}/stream/
 * Receives thinking messages during AI processing.
 * 
 * Adapted from useChatProgress.ts for XAI chat endpoint.
 */

import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { apiUrl } from "@app/const";
import { getAccessToken } from "@app/api/getAccessToken";
import { store } from "@app/redux/store";

// SSE connection timeout (30 seconds)
const SSE_CONNECTION_TIMEOUT_MS = 30000;
// SSE inactivity timeout (5 minutes)
const SSE_INACTIVITY_TIMEOUT_MS = 300000;
// Auto-reconnect settings (for flaky networks / proxy buffering)
const SSE_MAX_RECONNECT_ATTEMPTS = 6;
const SSE_RECONNECT_BASE_DELAY_MS = 750;
const SSE_RECONNECT_MAX_DELAY_MS = 10000;

export interface ThinkItemMessage {
  type: "think_item";
  title: string;
  description: string;
  step?: string;
  timestamp: string;
}

export interface CompleteMessage {
  type: "complete";
  result: {
    content: string;
    /** Plan result type in focus after this run (e.g. after switch_plan_result_type). */
    plan_result_type_id?: string;
    /** Display label for the plan result type (e.g. "Primary", "Proposed"). */
    plan_result_type_label?: string;
  };
  timestamp: string;
}

export interface ErrorMessage {
  type: "error";
  error: string;
  timestamp: string;
}

export interface ContentMessage {
  type: "content";
  content: string;
  step?: string;
  timestamp: string;
}

export interface ConnectedMessage {
  type: "connected";
  status: string;
}

export type XAIProgressMessage =
  | ThinkItemMessage
  | CompleteMessage
  | ErrorMessage
  | ContentMessage
  | ConnectedMessage;

export interface UseXAIChatProgressReturn {
  progress: XAIProgressMessage | null;
  isConnected: boolean;
  error: Error | null;
  currentStep: string | null;
  lastThinkItem: ThinkItemMessage | null;
  /**
   * Plain text streamed content captured from `event: text` SSE events.
   *
   * Polaris Chat publishes content to Redis Streams and the backend progress endpoint
   * replays it as `event: text` (not JSON).
   */
  streamedText: string;
  /** Plan result type ID from the last `complete` event (e.g. after in-chat switch). */
  lastCompletePlanResultTypeId: string | null;
  /** Plan result type label from the last `complete` event (e.g. "Primary", "Proposed"). */
  lastCompletePlanResultTypeLabel: string | null;
  close: () => void;
}

export const useXAIChatProgress = (
  requestId: string | null
): UseXAIChatProgressReturn => {
  const [progress, setProgress] = useState<XAIProgressMessage | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [lastThinkItem, setLastThinkItem] = useState<ThinkItemMessage | null>(
    null
  );
  const [streamedText, setStreamedText] = useState("");
  const [lastCompletePlanResultTypeId, setLastCompletePlanResultTypeId] =
    useState<string | null>(null);
  const [lastCompletePlanResultTypeLabel, setLastCompletePlanResultTypeLabel] =
    useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const reconnectAttemptRef = useRef(0);
  const completedRef = useRef(false);

  const close = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // IMPORTANT: Clear old progress/stream state *before* the next paint when requestId changes.
  // This avoids a race where a previous `complete` event can be briefly observed by UI effects
  // after `requestId` changes, causing the previous answer to be re-rendered for the new prompt.
  useLayoutEffect(() => {
    // Always close any existing connection when requestId changes (including -> null).
    close();

    // Reset per-request state immediately.
    setProgress(null);
    setCurrentStep(null);
    setError(null);
    setLastThinkItem(null);
    setStreamedText("");
    setLastCompletePlanResultTypeId(null);
    setLastCompletePlanResultTypeLabel(null);
    reconnectAttemptRef.current = 0;
    completedRef.current = false;
  }, [requestId, close]);

  useEffect(() => {
    if (!requestId) {
      return;
    }

    const connectSSE = async () => {
      const sseStartTime = Date.now();
      let lastDataReceivedTime = Date.now();
      let connectionTimeoutId: NodeJS.Timeout | null = null;
      let inactivityTimeoutId: NodeJS.Timeout | null = null;
      let firstThinkItemReceived = false;
      let messageCount = 0;

      const scheduleReconnect = (reason: string) => {
        if (completedRef.current) return;
        if (abortControllerRef.current?.signal.aborted) return;
        if (reconnectAttemptRef.current >= SSE_MAX_RECONNECT_ATTEMPTS) {
          setError(
            new Error(
              `SSE connection unstable (retries exhausted). Last reason: ${reason}`
            )
          );
          return;
        }

        const attempt = reconnectAttemptRef.current + 1;
        reconnectAttemptRef.current = attempt;
        const base = Math.min(
          SSE_RECONNECT_MAX_DELAY_MS,
          SSE_RECONNECT_BASE_DELAY_MS * Math.pow(2, attempt - 1)
        );
        const jitter = Math.floor(Math.random() * 250);
        const delay = base + jitter;

        console.warn(
          `[XAI SSE] Reconnecting in ${delay}ms (attempt ${attempt}/${SSE_MAX_RECONNECT_ATTEMPTS}) - ${reason}`
        );

        setIsConnected(false);
        if (connectionTimeoutId) clearTimeout(connectionTimeoutId);
        if (inactivityTimeoutId) clearTimeout(inactivityTimeoutId);
        // Abort any in-flight connection before retrying
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;

        setTimeout(() => {
          // Only reconnect if requestId is still active
          if (!completedRef.current) {
            connectSSE();
          }
        }, delay);
      };

      try {
        const accessToken = await getAccessToken(store);
        if (!accessToken) {
          console.error("[XAI SSE] No access token available");
          setError(new Error("No access token available"));
          return;
        }

        const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
        // IMPORTANT: Progress endpoint is always served from `/api/xai-chat/progress/...`
        // for both XAI Chat and Polaris Chat (shared Redis stream transport).
        const url = `${baseUrl}/api/xai-chat/progress/${requestId}/stream/`;
        
        console.log("[XAI SSE] Progress endpoint URL:", {
          baseUrl,
          fullUrl: url,
        });

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        console.log("[XAI SSE] Connecting to:", url);
        const fetchStartTime = Date.now();
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "text/event-stream",
            Authorization: `Bearer ${accessToken}`,
          },
          signal: abortController.signal,
        });

        const fetchEndTime = Date.now();
        console.log(
          `[XAI SSE] Connection established in ${
            fetchEndTime - fetchStartTime
          }ms, status: ${response.status}`
        );

        if (!response.ok) {
          console.error(
            `[XAI SSE] Connection failed with status ${response.status}`
          );
          // If upstream/proxy is buffering or timing out before sending the first SSE bytes,
          // we can see transient 5xx responses (commonly 502) during long-running jobs.
          // Auto-retry so the UI doesn't get stuck in a failed state.
          const status = response.status;
          if (status >= 500 && status <= 599) {
            scheduleReconnect(`upstream returned ${status}`);
            return;
          }

          setError(new Error(`SSE connection failed: ${response.status}`));
          setIsConnected(false);
          return;
        }

        setIsConnected(true);
        setError(null);

        const reader = response.body?.getReader();
        if (!reader) {
          console.error("[XAI SSE] No response body reader available");
          setError(new Error("No response body reader available"));
          return;
        }

        // Set connection timeout
        connectionTimeoutId = setTimeout(() => {
          if (!firstThinkItemReceived) {
            console.warn(
              `[XAI SSE] No data received within ${SSE_CONNECTION_TIMEOUT_MS}ms`
            );
            scheduleReconnect("no data within connection timeout");
          }
        }, SSE_CONNECTION_TIMEOUT_MS);

        // Set inactivity timeout
        inactivityTimeoutId = setTimeout(() => {
          console.warn("[XAI SSE] Closing connection due to inactivity");
          setIsConnected(false);
          abortController.abort();
          scheduleReconnect("inactivity timeout");
        }, SSE_INACTIVITY_TIMEOUT_MS);

        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();

          if (abortController.signal.aborted || done) {
            console.log(
              `[XAI SSE] Stream closed. Total messages: ${messageCount}`
            );
            setIsConnected(false);
            break;
          }

          // Update last data received time
          lastDataReceivedTime = Date.now();

          // Reset inactivity timeout
          if (inactivityTimeoutId) {
            clearTimeout(inactivityTimeoutId);
            inactivityTimeoutId = setTimeout(() => {
              console.warn("[XAI SSE] Closing connection due to inactivity");
              setIsConnected(false);
              abortController.abort();
            }, SSE_INACTIVITY_TIMEOUT_MS);
          }

          buffer += decoder.decode(value, { stream: true });

          // Process SSE events (separated by \n\n)
          const eventSeparator = "\n\n";
          let eventEndIndex = buffer.indexOf(eventSeparator);

          while (eventEndIndex !== -1) {
            const eventText = buffer.substring(0, eventEndIndex);
            buffer = buffer.substring(eventEndIndex + eventSeparator.length);

            // Extract event type and data from SSE format
            const lines = eventText.split("\n");
            let eventType = "";
            let eventData = "";

            for (const line of lines) {
              if (line.startsWith("event:")) {
                eventType = line.slice(6).trimStart();
              } else if (line.startsWith("data:")) {
                // Per SSE spec, a single leading space after "data:" is optional and should be stripped.
                // Do NOT trimStart() here, because streamed LLM output frequently relies on leading
                // whitespace/newlines for correct word spacing and markdown formatting.
                let dataContent = line.slice(5);
                if (dataContent.startsWith(" ")) {
                  dataContent = dataContent.slice(1);
                }
                eventData = eventData
                  ? `${eventData}\n${dataContent}`
                  : dataContent;
              }
            }

            if (eventData) {
              messageCount++;

              // Polaris content chunks are replayed as plain text events.
              // These are NOT JSON and should not be parsed as JSON.
              if (eventType === "text") {
                // Clear connection timeout on first message
                if (connectionTimeoutId) {
                  clearTimeout(connectionTimeoutId);
                  connectionTimeoutId = null;
                }
                setStreamedText((prev) => prev + eventData);
                // Don't set `progress` for text chunks; UI can use streamedText directly.
                eventEndIndex = buffer.indexOf(eventSeparator);
                continue;
              }

              try {
                const parsed = JSON.parse(eventData) as unknown;

                // Some backends/proxies may emit content chunks as JSON strings (e.g. `"hello"`)
                // or as JSON objects without a `type` field (e.g. `{"content": "..."}`).
                // Treat those as streamed text chunks.
                if (typeof parsed === "string") {
                  setStreamedText((prev) => prev + parsed);
                  eventEndIndex = buffer.indexOf(eventSeparator);
                  continue;
                }

                const message = parsed as Partial<XAIProgressMessage> & {
                  content?: string;
                };

                if (!("type" in message) || !message.type) {
                  // Ignore heartbeat/keepalive and other non-typed events that aren't content.
                  // (Backend should send heartbeats as SSE comments, but be defensive.)
                  if (eventType === "heartbeat" || eventType === "keepalive") {
                    eventEndIndex = buffer.indexOf(eventSeparator);
                    continue;
                  }

                  if (typeof message.content === "string") {
                    setStreamedText((prev) => prev + message.content);
                  } else {
                    // If this came in as a named event (not plain text), it's almost certainly
                    // a control/diagnostic payload. Don't render it into the user-visible stream.
                    if (eventType) {
                      eventEndIndex = buffer.indexOf(eventSeparator);
                      continue;
                    }

                    // Fallback: treat unknown JSON payload as text only for unnamed events.
                    setStreamedText((prev) => prev + eventData);
                  }
                  eventEndIndex = buffer.indexOf(eventSeparator);
                  continue;
                }

                console.log(
                  `[XAI SSE] Message #${messageCount}, type: ${message.type}`
                );

                // Clear connection timeout on first message
                if (connectionTimeoutId) {
                  clearTimeout(connectionTimeoutId);
                  connectionTimeoutId = null;
                }

                // Handle different message types
                if (message.type === "connected") {
                  console.log("[XAI SSE] Connection confirmed by server");
                } else if (
                  message.type === "complete" ||
                  message.type === "error"
                ) {
                  console.log(
                    `[XAI SSE] Received ${message.type}. Closing connection.`
                  );
                  if (message.type === "complete") {
                    const result = (message as CompleteMessage).result;
                    if (result?.plan_result_type_id) {
                      setLastCompletePlanResultTypeId(
                        String(result.plan_result_type_id)
                      );
                    }
                    if (result?.plan_result_type_label != null) {
                      setLastCompletePlanResultTypeLabel(
                        String(result.plan_result_type_label)
                      );
                    }
                  }
                  setProgress(message as XAIProgressMessage);
                  completedRef.current = true;
                  setIsConnected(false);
                  if (connectionTimeoutId) clearTimeout(connectionTimeoutId);
                  if (inactivityTimeoutId) clearTimeout(inactivityTimeoutId);
                  abortController.abort();
                  return;
                } else if (message.type === "think_item") {
                  // Track first think_item timing
                  if (!firstThinkItemReceived) {
                    firstThinkItemReceived = true;
                    const thinkItemTime = Date.now();
                    const timeFromStart = thinkItemTime - sseStartTime;
                    console.log(
                      `[XAI SSE] First think_item received after ${timeFromStart}ms`
                    );
                  }

                  console.log("[XAI SSE] Setting think_item:", {
                    title: (message as ThinkItemMessage).title,
                    description: (message as ThinkItemMessage).description,
                    step: (message as ThinkItemMessage).step,
                  });
                  setProgress(message as XAIProgressMessage);
                  setLastThinkItem(message as ThinkItemMessage);
                  if ((message as ThinkItemMessage).step) {
                    setCurrentStep((message as ThinkItemMessage).step || null);
                  }
                } else if (message.type === "content") {
                  // Backward compatibility: if backend ever sends JSON content messages
                  // (event: content), accumulate into streamedText as well.
                  setStreamedText((prev) => prev + ((message as ContentMessage).content || ""));
                }
              } catch (err) {
                // If it's not JSON, treat it as a plain text chunk (common when `event:` is omitted).
                setStreamedText((prev) => prev + eventData);
              }
            }

            eventEndIndex = buffer.indexOf(eventSeparator);
          }
        }
      } catch (err) {
        // Clean up timeouts
        if (connectionTimeoutId) clearTimeout(connectionTimeoutId);
        if (inactivityTimeoutId) clearTimeout(inactivityTimeoutId);

        if (err instanceof Error && err.name === "AbortError") {
          console.log("[XAI SSE] Connection aborted (expected)");
          setIsConnected(false);
          return;
        }

        console.error("[XAI SSE] Connection error:", err);
        setIsConnected(false);
        // Common in development: intermittent QUIC/HTTP3 errors, transient network failures.
        // Retry automatically so the UI doesn't look "stuck".
        scheduleReconnect(
          err instanceof Error ? err.message : `unknown error: ${String(err)}`
        );
      }
    };

    connectSSE();

    return () => {
      close();
    };
  }, [requestId, close]);

  return {
    progress,
    isConnected,
    error,
    currentStep,
    lastThinkItem,
    streamedText,
    lastCompletePlanResultTypeId,
    lastCompletePlanResultTypeLabel,
    close,
  };
};
